/**
 * TemplateLibrary — list of approved SOP/WI/BMR templates the org can clone
 *                   when starting a new document (V18 / WF-033).
 *
 * Owner: Phase 7+ — Department hierarchy model.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PageHero } from '@edms/design-system';
import { useSopTemplates, useTransitionTemplate, useCloneTemplate, type SopTemplate } from '../../api/orgHooks';
import { useSession } from '../../auth/SessionProvider';
import { NewTemplateDialog } from './NewTemplateDialog';
import { downloadAuthed } from '../../api/client';
import styles from './TemplateLibrary.module.css';

// Map a stored artefact MIME to a file extension for the download filename.
// The server's Content-Disposition isn't readable cross-origin (CORS doesn't
// expose it by default), so we build a correct filename client-side. (F0-DL.)
const MIME_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'image/png': 'png',
  'image/jpeg': 'jpg',
};
function artifactFilename(code: string, mime?: string | null): string {
  const safe = (code || 'template').replace(/[^\w.\-]/g, '_');
  const ext = (mime && MIME_EXT[mime]) || (mime ? mime.split('/').pop()?.replace(/[^\w]/g, '') : '') || 'bin';
  return `${safe}.${ext}`;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  DRAFT:     { label: 'Draft',     cls: 'badgeMuted'   },
  IN_REVIEW: { label: 'In review', cls: 'badgeWarn'    },
  APPROVED:  { label: 'Approved',  cls: 'badgeSuccess' },
  WITHDRAWN: { label: 'Withdrawn', cls: 'badgeDanger'  },
};

function TransitionButtons({ tpl }: { tpl: SopTemplate }) {
  const sessionCtx = useSession();
  const m = useTransitionTemplate(tpl.templateId);
  const clone = useCloneTemplate(tpl.templateId);
  const userId = sessionCtx.session?.userId ?? '';
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignSelf: 'flex-start' }}>
      {tpl.status === 'DRAFT' && (
        <Button size="sm" variant="secondary" isLoading={m.isPending}
          onClick={() => m.mutate({ to: 'IN_REVIEW' })}>Submit for review</Button>
      )}
      {tpl.status === 'IN_REVIEW' && (
        <Button size="sm" variant="primary" isLoading={m.isPending}
          onClick={() => m.mutate({ to: 'APPROVED', signerUserId: userId })}>Approve (QA sign)</Button>
      )}
      {/* C-11 — clone any template into a new DRAFT (saves re-authoring). */}
      <Button size="sm" variant="secondary" isLoading={clone.isPending}
        onClick={() => clone.mutate()} title="Create a new DRAFT template from this one">Clone</Button>
      {(tpl.status === 'DRAFT' || tpl.status === 'IN_REVIEW' || tpl.status === 'APPROVED') && (
        <Button size="sm" variant="destructive" isLoading={m.isPending}
          onClick={() => m.mutate({ to: 'WITHDRAWN' })}>Withdraw</Button>
      )}
    </div>
  );
}

/** Download button that fetches the artefact WITH the Bearer token (a plain
 *  <a href> can't carry it) and blob-downloads it. (drafts/61 F0-DL.) */
function DownloadArtifactButton({ tpl }: { tpl: SopTemplate }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  async function go() {
    setBusy(true);
    setErr(undefined);
    try {
      await downloadAuthed(
        `/api/v1/sop-templates/${tpl.templateId}/artifact`,
        artifactFilename(tpl.code, tpl.artifactMime),
      );
    } catch (e) {
      setErr(
        (e as { detail?: string; message?: string }).detail
          ?? (e as Error).message
          ?? t('templateLibrary.downloadFailed', { defaultValue: 'Download failed.' }),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={() => void go()}
        disabled={busy}
        className={`${styles.artifactBtn} ${styles.artifactBtnSecondary}`}
        aria-label={t('templateLibrary.downloadAria', { defaultValue: 'Download template artefact' })}
      >
        <span aria-hidden="true">⬇</span>
        {busy
          ? t('templateLibrary.downloading', { defaultValue: 'Downloading…' })
          : t('templateLibrary.download', { defaultValue: 'Download' })}
      </button>
      {err && (
        <span role="alert" className={styles.artifactError}>
          {err}
        </span>
      )}
    </>
  );
}

export function TemplateLibrary() {
  const { t } = useTranslation();
  const [docType, setDocType] = useState<string>('');
  const [newOpen, setNewOpen] = useState(false);
  const [, setStatus] = useState<string>('APPROVED');
  const q = useSopTemplates(docType || undefined);

  const docTypeOptions = useMemo(() => {
    const set = new Set<string>();
    (q.data?.items ?? []).forEach((t) => set.add(t.docTypeCode));
    return ['', ...Array.from(set)];
  }, [q.data]);

  return (
    <div className={styles.page}>
      <PageHero
        eyebrow={t('templateLibrary.eyebrow', { defaultValue: 'Documents' })}
        title={t('templateLibrary.title', { defaultValue: 'SOP Template Library' })}
        subtitle={t('templateLibrary.subtitle', {
          defaultValue:
            'QA-approved document templates. Authors clone these when starting a new SOP/WI/BMR — pre-fills doc type, department, and source content.',
        })}
        actions={
          <Button variant="primary" onClick={() => setNewOpen(true)} leadingIcon="+">
            {t('templateLibrary.newTemplate', { defaultValue: 'New template' })}
          </Button>
        }
      />

      <NewTemplateDialog isOpen={newOpen} onClose={() => setNewOpen(false)} onCreated={() => setStatus('DRAFT')} />

      <section className={styles.toolbar}>
        <label className={styles.filterLabel}>
          {t('templateLibrary.filterDocType', { defaultValue: 'Doc type' })}
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className={styles.filterSelect}>
            <option value="">{t('templateLibrary.allTypes', { defaultValue: 'All types' })}</option>
            {docTypeOptions.filter(Boolean).map((dt) => (
              <option key={dt} value={dt}>{dt}</option>
            ))}
          </select>
        </label>
        <div className={styles.toolbarRight}>
          {q.data && (
            <span className={styles.count}>
              {t('templateLibrary.count', { defaultValue: '{{n}} templates', n: q.data.items.length })}
            </span>
          )}
        </div>
      </section>

      {q.isLoading && <div className={styles.empty}>{t('templateLibrary.loading', { defaultValue: 'Loading…' })}</div>}
      {q.isError && (
        <div className={styles.empty} style={{ color: 'var(--color-status-error, #B91C1C)' }}>
          {t('templateLibrary.error', { defaultValue: 'Could not load templates.' })}
        </div>
      )}

      {q.data && q.data.items.length > 0 && (
        <div className={styles.grid}>
          {q.data.items.map((tpl) => {
            const badge = STATUS_BADGE[tpl.status] ?? STATUS_BADGE['DRAFT'] ?? { label: tpl.status, cls: 'badgeMuted' };
            return (
              <article key={tpl.templateId} className={styles.card}>
                <header className={styles.cardHeader}>
                  <span className={styles.code}>{tpl.code}</span>
                  <span className={`${styles.badge} ${styles[badge.cls]}`}>{badge.label}</span>
                </header>
                <h3 className={styles.cardTitle}>{tpl.title}</h3>
                {tpl.description && <p className={styles.cardDesc}>{tpl.description}</p>}
                <dl className={styles.meta}>
                  <div className={styles.metaRow}>
                    <dt>{t('templateLibrary.field.docType', { defaultValue: 'Doc type' })}</dt>
                    <dd>{tpl.docTypeCode}</dd>
                  </div>
                  <div className={styles.metaRow}>
                    <dt>{t('templateLibrary.field.dept', { defaultValue: 'Owner dept' })}</dt>
                    <dd>{tpl.owningDepartmentName ?? '— site-wide —'}</dd>
                  </div>
                  <div className={styles.metaRow}>
                    <dt>{t('templateLibrary.field.version', { defaultValue: 'Version' })}</dt>
                    <dd>v{tpl.versionMajor}.{tpl.versionMinor}</dd>
                  </div>
                  {tpl.approvedAtUtc && (
                    <div className={styles.metaRow}>
                      <dt>{t('templateLibrary.field.approved', { defaultValue: 'Approved' })}</dt>
                      <dd>{new Date(tpl.approvedAtUtc).toLocaleDateString()}</dd>
                    </div>
                  )}
                  {tpl.artifactSizeBytes && (
                    <div className={styles.metaRow}>
                      <dt>{t('templateLibrary.field.size', { defaultValue: 'Artefact' })}</dt>
                      <dd className={styles.artifactCell}>
                        <span className={styles.artifactMeta}>
                          {Math.round(tpl.artifactSizeBytes / 1024)} KB
                          {tpl.artifactMime && (
                            <> · {tpl.artifactMime.split('/').pop()}</>
                          )}
                        </span>
                        {tpl.artifactRef && (
                          <div className={styles.artifactActions}>
                            <DownloadArtifactButton tpl={tpl} />
                            <a
                              href={`/documents/${encodeURIComponent(tpl.templateId)}/edit`}
                              className={`${styles.artifactBtn} ${styles.artifactBtnPrimary}`}
                            >
                              <span aria-hidden="true">✎</span>
                              {t('templateLibrary.editInApp', { defaultValue: 'Edit in-app' })}
                            </a>
                          </div>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
                <TransitionButtons tpl={tpl} />
              </article>
            );
          })}
        </div>
      )}

      {q.data && q.data.items.length === 0 && (
        <div className={styles.empty}>{t('templateLibrary.emptyMatch', { defaultValue: 'No templates match this filter.' })}</div>
      )}
    </div>
  );
}
